import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { Form, Modal } from 'react-bootstrap';
import { createSelector } from '@reduxjs/toolkit';
import { useHistory } from 'react-router-dom';
import type { IClientContract, IRestermAgentRequest } from '@/api/types';
import type { IAgent, IContract } from '@/store/types';

import { formatDate, formatDateToUtc, formatEffectiveBlock, formData, parseError } from '@/helpers';
import { useAppDispatch, useAppSelector, useModalOptions, usePathParam, useTimezone } from '@/hooks';
import { Input, ModalFooter, Select, Textarea } from '@/components/common';
import { FULFILLED_ACTION, MONTH_DAY_YEAR_FORMAT } from '@/config/variables';
import { OpenNew } from '@/assets/icons';
import { media } from '@/styles/theme';
import { selectClientRestermReasons, selectIsPendingRestermActions } from '@/store/selectors/contractSelectors';
import { EffectiveType, PathParams, RestermReasonType } from '@/store/enums';
import { removeAgent } from '@/store/slices/contractSlice';
import { schemaRemoveAgent } from '@/components/client/contracts/yup-schemas';
import { getDateFromBlock } from '@/helpers/block';

interface IProps {
  agent: IAgent;
  contract?: IContract | IClientContract;
  handleClose: (shouldDrawerClose?: boolean) => void;
  fetchAgent?: () => Promise<unknown> | undefined;
}

interface IRemoveForm {
  agentId: number;
  effectiveDate: string;
  reason: string;
  terminationLetter: string;
}

const removeAgentModalSelector = createSelector(
  selectClientRestermReasons,
  selectIsPendingRestermActions,
  (restermReasons, isPendingResterm) => ({
    restermReasons,
    isPending: isPendingResterm,
  }),
);

const RemoveAgentModal = ({ agent, contract, handleClose, fetchAgent }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { restermReasons, isPending } = useAppSelector(removeAgentModalSelector);
  const { removeSuspendOptions: removeOptions } = useModalOptions(agent, contract);
  const { timezoneName } = useTimezone();

  const [contractId] = usePathParam(PathParams.contractId);
  const [agentContractId] = usePathParam(PathParams.spContractId);

  const [isFetchAgentPending, setIsFetchAgentPending] = useState(false);

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, touchedFields },
  } = useForm<IRemoveForm>({
    defaultValues: {
      agentId: removeOptions?.userId,
      effectiveDate: formatDate(new Date(), MONTH_DAY_YEAR_FORMAT),
      reason: '',
      terminationLetter: '',
    },
    resolver: yupResolver(schemaRemoveAgent),
  });

  const formDataOptions = {
    register,
    errors,
    touchedFields,
  };

  const historyLink = useMemo(() => {
    if (!contractId) return;
    const params = new URLSearchParams(history.location.search);
    params.set(PathParams.spTab, 'history');
    params.set(PathParams.spContractId, agentContractId?.toString() ?? contractId.toString());
    params.set(PathParams.spId, agent.userId.toString());
    return `${location.origin}${history.location.pathname}?${params.toString()}`;
  }, [agent.userId, agentContractId, contractId, history.location.pathname, history.location.search]);

  const handleRemove: SubmitHandler<IRemoveForm> = async data => {
    if (!removeOptions) return;
    const options: IRestermAgentRequest = {
      ...removeOptions,
      effectiveDate: formatDateToUtc(new Date()).toISOString(),
      effectiveBlock: formatEffectiveBlock('', EffectiveType.immediately),
      reasonId: Number(data.reason),
      reasonText: restermReasons?.find(item => item.value === Number(data.reason))?.label || '',
      comment: data.terminationLetter,
    };

    const response = await dispatch(removeAgent(options));
    if (fetchAgent) {
      setIsFetchAgentPending(true);
      await fetchAgent();
    }
    if (response.meta.requestStatus === FULFILLED_ACTION) {
      toast.success(t('clientGigs.modal.remove.success', { name: `${agent.firstName} ${agent.lastName}` }));
      handleClose(true);
    } else {
      toast.error(parseError(response.payload));
      handleClose();
    }
    setIsFetchAgentPending(false);
  };

  return (
    <Form onSubmit={handleSubmit(handleRemove)}>
      <Modal.Body>
        <Container>
          <TopContainer>
            <div className="wd-50">
              <Input
                className="name"
                name="name"
                type="text"
                disabled={true}
                value={`${agent.firstName} ${agent.lastName}`}
                label={t`clientGigs.modal.remove.label.name`}
              />
            </div>
            <div className="d-flex wd-50">
              <Input
                className="pe-3"
                name="startDate"
                type="text"
                label={t`clientGigs.modal.remove.label.startDate`}
                disabled={true}
                value={formatDate(getDateFromBlock(agent.contractAssnStartBlock, timezoneName), MONTH_DAY_YEAR_FORMAT)}
              />
              <Input
                name="endDate"
                type="text"
                label={t`clientGigs.modal.remove.label.endDate`}
                disabled={true}
                value={formatDate(new Date(), MONTH_DAY_YEAR_FORMAT)}
              />
            </div>
          </TopContainer>
          <Input
            name="contractName"
            type="text"
            label={t`clientGigs.modal.remove.label.gigName`}
            disabled={true}
            value={contract?.displayName ?? ''}
          />
          <div className="divider" />
          <Select
            name="reason"
            label={t`clientGigs.modal.remove.label.reason`}
            control={control}
            required
            {...formData('reason', formDataOptions)}
            options={(restermReasons || []).filter(
              reason => reason.type === RestermReasonType.termination_and_suspension,
            )}
            className="select"
          />
          <Textarea
            name="terminationLetter"
            label={t`clientGigs.modal.remove.label.terminationLetter`}
            control={control}
            {...formData('terminationLetter', formDataOptions)}
            required
            className="textarea"
          />
        </Container>
      </Modal.Body>
      <ModalFooter
        info={<RemoveAgentModalFooterInfo href={historyLink} />}
        onSecondaryBtnClick={handleClose}
        primaryDisabled={isPending || isFetchAgentPending}
        secondaryDisabled={isPending || isFetchAgentPending}
        primaryBtnText={t`clientGigs.modal.remove.btnSubmit`}
      />
    </Form>
  );
};

const RemoveAgentModalFooterInfo = ({ href = '#' }: { href?: string }) => {
  const { t } = useTranslation();
  return (
    <AgentHistoryLink href={href} target="_blank">
      {t`clientGigs.modal.remove.historyLink`}
      <OpenNewIcon />
    </AgentHistoryLink>
  );
};

RemoveAgentModal.FooterInfo = RemoveAgentModalFooterInfo;

export { RemoveAgentModal };

const Container = styled.div.attrs({ className: 'd-flex flex-column flex-grow-1' })`
  .divider {
    border-bottom: 1px solid #d8d8d8;
    margin: 2rem 0 1.5rem;
  }

  .select {
    margin-bottom: 1rem;

    & > select {
      color: #343a40;
      background-color: #ffffff;
    }
  }

  .textarea {
    padding: 12px 16px;
    background-color: #ffffff;
    color: #343a40;
  }
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${media.laptop} {
    flex-direction: row;
  }

  .wd-50 {
    ${media.laptop} {
      width: 50%;
    }
    display: flex;

    fieldset {
      flex: 1 0 50%;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      box-sizing: border-box;
    }
  }

  .name {
    padding-right: 0;

    ${media.laptop} {
      padding-right: 3rem;
    }
  }
`;

const OpenNewIcon = styled.div`
  height: 12px;
  width: 12px;
  margin-left: 0.25rem;

  background-image: url(${OpenNew});
  background-repeat: no-repeat;
  background-size: contain;
  cursor: pointer;
`;

const AgentHistoryLink = styled.a.attrs({ className: 'd-flex align-items-center' })`
  color: #0087dd;
  text-decoration: underline;
  font-size: 12px;
`;
